import { apiFetch } from '../../../../lib/api';

export type OrganizationServiceTabCode = 'marketing' | 'platform' | 'assets_design';
export type AgenticPlanningMode =
  | 'onboarding_initial_build'
  | 'existing_client_recurring_planning';
export type AgenticPlanningInitiativeType = 'one_time' | 'recurring';
export type AgenticPlanningInitiativeStatus =
  | 'drafting'
  | 'active'
  | 'paused'
  | 'archived';
export type AgenticPlanningProgramType = 'product' | 'plan';
export type AgenticPlanningProgramStatus =
  | 'drafting'
  | 'active'
  | 'paused'
  | 'archived';
export type AgenticPlanningProgramScopeKind = 'offering' | 'lane';
export type AgenticPlanningInitiativeCadenceUnit =
  | 'weekly'
  | 'biweekly'
  | 'monthly'
  | 'ad_hoc'
  | 'unspecified';
export type AgenticPlanningRecurringSeriesStatus =
  | 'active'
  | 'paused'
  | 'archived';
export type AgenticPlanningClassification =
  | 'full_build'
  | 'discovery_first'
  | 'insufficient_input_review'
  | 'update_existing_project'
  | 'create_new_project'
  | 'backlog_only'
  | 'no_change'
  | 'review_required';

export type AgenticDraftReviewStatus =
  | 'review_required'
  | 'approved_draft'
  | 'rejected_draft'
  | 'needs_revision'
  | 'superseded';

export type AgenticDraftReviewAction =
  | 'approve'
  | 'reject'
  | 'request_revision'
  | 'mark_superseded';

export type AgenticApplyStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'partially_failed'
  | 'failed';

export type AgenticApplyOperationStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'deferred';

export type AgenticTriggerStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped';

export type AgenticTriggerEventStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'deduped';

export type AgenticPlanningAttentionItemType =
  | 'draft_needs_review'
  | 'approved_ready_to_apply'
  | 'approved_apply_blocked'
  | 'apply_failed'
  | 'apply_partially_failed'
  | 'new_note_after_latest_plan'
  | 'plan_needs_revision';

export type AgenticPlanningAttentionStatus =
  | 'open'
  | 'resolved'
  | 'dismissed'
  | 'obsolete';

export type AgenticPlanningAttentionPriority = 'high' | 'medium' | 'low';

export type AgenticPlanningAttentionAction = 'dismiss' | 'resolve';

export type AgenticPlanningSpecialistStage = 'discovery' | 'planning' | 'validation';

export type AgenticPlanningSpecialistInfluenceStatus =
  | 'applied'
  | 'partially_applied'
  | 'ignored';

export type AgenticPlanningRunComparisonType =
  | 'arbitrary_pair'
  | 'latest_vs_previous'
  | 'latest_vs_latest_approved'
  | 'nearest_meaningful_baseline';

export type AgenticPlanningRunComparisonModeRelation = 'same_mode' | 'cross_mode';

export type AgenticPlanningRunComparisonComparabilityLevel = 'high' | 'medium' | 'low';

export type AgenticPlanningRunComparisonBaseline =
  | 'previous'
  | 'latest_approved'
  | 'nearest_meaningful';

export type AgenticPlanningRunComparisonBaselineStrategy =
  | 'latest_approved_same_mode'
  | 'previous_same_mode'
  | 'latest_approved_any_mode'
  | 'previous_any_mode'
  | 'none';

export type OrganizationAIAgentScheduleEvent = {
  id: string;
  orgId: string | null;
  meetingType: string;
  title: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  status: 'scheduled' | 'canceled' | 'completed' | 'no_show';
};

export type OrganizationAIAgentOnboardingProfile = Record<string, string | null> & {
  businessOverview?: string | null;
  clientGoals?: string | null;
  servicesInScope?: string | null;
  projectOrEngagementType?: string | null;
  immediatePriorities?: string | null;
  targetUsersAndRoles?: string | null;
  stakeholders?: string | null;
  coreWorkflowsJourneys?: string | null;
  firstMilestoneDeliverables?: string | null;
  currentState?: string | null;
  constraintsDependencies?: string | null;
  clientDependencies?: string | null;
  blockingClarifications?: string | null;
  platformTargets?: string | null;
};

export type OrganizationAIAgentMeetingNote = {
  id: string;
  eventId: string;
  orgId: string | null;
  summary: string | null;
  clientUpdates: string | null;
  decisions: string | null;
  nextSteps: string | null;
  internalContext: string | null;
  onboardingProfile?: OrganizationAIAgentOnboardingProfile | null;
  createdAt: string;
  updatedAt: string;
};

export type OrganizationAIAgentKickoffSource = {
  event: OrganizationAIAgentScheduleEvent;
  note: OrganizationAIAgentMeetingNote | null;
};

export type OrganizationAIAgentRecurringNoteSource = {
  event: OrganizationAIAgentScheduleEvent;
  note: OrganizationAIAgentMeetingNote;
};

export type OrganizationAIAgentPlanningSources = {
  kickoffSource: OrganizationAIAgentKickoffSource | null;
  recurringNoteSources: OrganizationAIAgentRecurringNoteSource[];
};

export type AgenticSourceArtifact = {
  artifactId: string;
  artifactType: 'onboarding_note' | 'onboarding_profile' | 'client_profile' | 'recurring_note';
  sourceSystem: string;
  sourceRef: string;
  orgId: string;
  clientId: string;
  capturedAt: string;
  normalizedText: string;
  meetingType?: string | null;
  title?: string | null;
  internalContext?: string | null;
  structuredFields?: Record<string, unknown>;
  evidenceRefs: string[];
};

export type AgenticSourceArtifactBundle = {
  bundleId: string;
  mode: AgenticPlanningMode;
  orgId: string;
  clientId: string;
  artifacts: AgenticSourceArtifact[];
  assembledAt: string;
  selectedEventIds?: string[];
  notes?: string[];
};

export type AgenticPlanningDecision = {
  decisionId: string;
  decisionType:
    | 'create_new_project'
    | 'create_project_series'
    | 'update_existing_project'
    | 'backlog_only'
    | 'discovery_first'
    | 'insufficient_input_review'
    | 'review_required'
    | 'needs_human_review'
    | 'out_of_scope'
    | 'no_change';
  subjectKey: string;
  rationale: string;
  evidenceRefs: string[];
  confidence: number;
  requiresManualApproval: boolean;
  affectedProjectKeys: string[];
  affectedLaneKeys?: string[];
  affectedExistingProjectIds?: string[];
  affectedExistingTicketIds?: string[];
  notes: string[];
};

export type AgenticPlanWorkstream = {
  key: string;
  title: string;
  objective: string;
  status: 'proposed' | 'in_scope' | 'out_of_scope';
  serviceCode?: string | null;
  offeringCodes: string[];
  evidenceRefs: string[];
  assumptions: string[];
};

export type AgenticPlanProject = {
  key: string;
  workstreamKey: string;
  name: string;
  objective: string;
  brief: string;
  status: 'proposed';
  changeType?: 'create' | 'update' | 'backlog_addition';
  existingProjectId?: string | null;
  serviceCode?: string | null;
  offeringCode?: string | null;
  offeringSubcategoryId?: string | null;
  clientSubscriptionId?: string | null;
  teamNodeId?: string | null;
  executionServiceCode?: string | null;
  executionOfferingCode?: string | null;
  executionOfferingSubcategoryId?: string | null;
  supportingTeamNodeIds: string[];
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  evidenceRefs: string[];
  milestoneKeys: string[];
};

export type AgenticPlanMilestone = {
  key: string;
  projectKey: string;
  title: string;
  existingMilestoneId?: string | null;
  outcome?: string | null;
  definitionOfDone?: string | null;
  plannedStartAt?: string | null;
  plannedEndAt?: string | null;
  evidenceRefs: string[];
  deliverableKeys: string[];
  ticketKeys: string[];
};

export type AgenticPlanDeliverable = {
  key: string;
  projectKey: string;
  milestoneKey: string;
  title: string;
  description?: string | null;
  ownerHint?: string | null;
  acceptanceCriteria: string[];
  clientFacing: boolean;
  evidenceRefs: string[];
};

export type AgenticPlanTicket = {
  key: string;
  projectKey: string;
  milestoneKey: string;
  title: string;
  description: string;
  changeType?: 'create' | 'update' | 'backlog_addition';
  existingTicketId?: string | null;
  serviceCode?: string | null;
  offeringCode?: string | null;
  offeringSubcategoryId?: string | null;
  clientSubscriptionId?: string | null;
  proposedExecutionTeamNodeId?: string | null;
  executionServiceCode?: string | null;
  executionOfferingCode?: string | null;
  executionOfferingSubcategoryId?: string | null;
  supportingTeamNodeIds: string[];
  dependsOnTicketKeys: string[];
  acceptanceCriteria: string[];
  estimateMinutes?: number | null;
  evidenceRefs: string[];
  doneCriteria?: string | null;
};

export type AgenticPackagingLane = {
  laneKey: string;
  subscriptionId?: string | null;
  serviceCode?: string | null;
  deliveryPattern:
    | 'single_project'
    | 'project_series'
    | 'single_project_with_phases'
    | 'review_required';
  planningSource?:
    | 'subscription_contract'
    | 'catalog_default'
    | 'catalog_derived'
    | 'local_fallback'
    | 'service_heuristic'
    | null;
  metadataDriven: boolean;
  fallbackUsed: boolean;
  sourceRef?: string | null;
  blueprintKey?: string | null;
  defaultWorkstreamKey?: string | null;
  milestoneGroupingHint?: string | null;
  contractStatus: 'valid' | 'partial' | 'invalid';
  packagingRule: string;
  projectNameRule?: string | null;
  issues: string[];
};

export type AgenticPackagingMetadata = {
  overallDeliveryPattern:
    | 'single_project'
    | 'project_series'
    | 'single_project_with_phases'
    | 'review_required';
  deterministic: boolean;
  requiresManualReview: boolean;
  lanes: AgenticPackagingLane[];
  notes: string[];
};

export type AgenticPurchasedLaneSummary = {
  laneKey: string;
  subscriptionId: string;
  serviceCode: string;
  serviceTitle?: string | null;
  subscriptionSource?: string | null;
  tierSkuId?: string | null;
  selectedOfferingCodes: string[];
  selectedOfferings: string[];
  primaryTeamNodeId?: string | null;
  primaryTeamLabel?: string | null;
  ownershipSource?:
    | 'subscription_contract'
    | 'catalog_default'
    | 'local_fallback'
    | 'routing_heuristic'
    | 'service_catalog'
    | null;
  ownershipSourceRef?: string | null;
  ownershipRationale?: string | null;
  ownershipContractStatus?: 'valid' | 'partial' | 'invalid' | null;
  deliveryPattern?:
    | 'single_project'
    | 'project_series'
    | 'single_project_with_phases'
    | 'review_required'
    | null;
  planningSource?:
    | 'subscription_contract'
    | 'catalog_default'
    | 'catalog_derived'
    | 'local_fallback'
    | 'service_heuristic'
    | null;
  blueprintKey?: string | null;
  coverageStatus: 'metadata_driven' | 'fallback' | 'unresolved';
  metadataDriven: boolean;
  fallbackUsed: boolean;
  fallbackReason?: string | null;
  fallbackDisposition: 'not_applicable' | 'temporary_safety_net' | 'readiness_blocker';
  blockingFrontendReadiness: boolean;
  requiredOwnershipFields: string[];
  presentOwnershipFields: string[];
  missingOwnershipFields: string[];
  issues: string[];
};

export type AgenticExecutionLaneSummary = {
  laneKey: string;
  subscriptionId: string;
  serviceCode: string;
  primaryTeamNodeId?: string | null;
  primaryTeamLabel?: string | null;
  primaryTeamSource?: string | null;
  supportingTeamNodeIds: string[];
  supportingTeamLabels?: Array<string | null>;
  reviewRequired: boolean;
  immediateProjectKeys: string[];
  immediateMilestoneKeys: string[];
  immediateTicketKeys: string[];
  backlogProjectKeys: string[];
  notes: string[];
};

export type AgenticPlanningRecurringSeriesSummary = {
  recurringSeriesId: string;
  programId: string;
  initiativeId: string;
  orgId: string;
  title: string;
  cadenceLabel?: string | null;
  cadenceUnit?: AgenticPlanningInitiativeCadenceUnit | null;
  cadenceEvery?: number | null;
  status: AgenticPlanningRecurringSeriesStatus;
  originRunId?: string | null;
  latestRunId?: string | null;
  latestAppliedRunId?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgenticPlanningProgramSummary = {
  programId: string;
  legacyInitiativeId: string;
  orgId: string;
  primaryLaneKey: string;
  primarySubscriptionId: string;
  primaryServiceCode?: string | null;
  primaryServiceTitle?: string | null;
  primaryScopeKind: AgenticPlanningProgramScopeKind;
  primaryScopeCode: string;
  primaryScopeLabel: string;
  primaryTeamNodeId?: string | null;
  primaryTeamLabel?: string | null;
  additionalLaneKeys: string[];
  title: string;
  objective: string;
  programType: AgenticPlanningProgramType;
  cadenceLabel?: string | null;
  cadenceUnit?: AgenticPlanningInitiativeCadenceUnit | null;
  cadenceEvery?: number | null;
  status: AgenticPlanningProgramStatus;
  originRunId?: string | null;
  latestRunId?: string | null;
  latestApprovedRunId?: string | null;
  currentDraftRunId?: string | null;
  latestAppliedRunId?: string | null;
  linkedProjectIds: string[];
  linkedTicketIds: string[];
  workstreamKeys: string[];
  recurringSeries: AgenticPlanningRecurringSeriesSummary[];
  createdAt: string;
  updatedAt: string;
};

export type AgenticPlanningInitiativeSummary = {
  initiativeId: string;
  programId?: string;
  orgId: string;
  primaryLaneKey: string;
  primarySubscriptionId: string;
  primaryServiceCode?: string | null;
  primaryServiceTitle?: string | null;
  primaryScopeKind?: AgenticPlanningProgramScopeKind;
  primaryScopeCode?: string;
  primaryScopeLabel?: string;
  additionalLaneKeys: string[];
  title: string;
  objective: string;
  initiativeType: AgenticPlanningInitiativeType;
  programType?: AgenticPlanningProgramType;
  cadenceLabel?: string | null;
  cadenceUnit?: AgenticPlanningInitiativeCadenceUnit | null;
  cadenceEvery?: number | null;
  status: AgenticPlanningInitiativeStatus;
  originRunId?: string | null;
  latestRunId?: string | null;
  latestApprovedRunId?: string | null;
  currentDraftRunId?: string | null;
  latestAppliedRunId?: string | null;
  linkedProjectIds: string[];
  linkedTicketIds: string[];
  workstreamKeys: string[];
  recurringSeries: AgenticPlanningRecurringSeriesSummary[];
  createdAt: string;
  updatedAt: string;
};

export type AgenticPlanningRunInitiativeSummary = {
  primaryInitiativeId?: string | null;
  primaryInitiativeTitle?: string | null;
  primaryLaneKey?: string | null;
  primarySubscriptionId?: string | null;
  primaryScopeKind?: AgenticPlanningProgramScopeKind | null;
  primaryScopeCode?: string | null;
  primaryScopeLabel?: string | null;
  initiativeType?: AgenticPlanningInitiativeType | null;
  programType?: AgenticPlanningProgramType | null;
  initiativeIds: string[];
  programIds?: string[];
  recurringSeriesIds: string[];
  initiativeCount: number;
};

export type AgenticPlanningRunProgramSummary = {
  primaryProgramId?: string | null;
  primaryProgramTitle?: string | null;
  primaryLaneKey?: string | null;
  primarySubscriptionId?: string | null;
  primaryScopeKind?: AgenticPlanningProgramScopeKind | null;
  primaryScopeCode?: string | null;
  primaryScopeLabel?: string | null;
  primaryTeamNodeId?: string | null;
  primaryTeamLabel?: string | null;
  programType?: AgenticPlanningProgramType | null;
  programIds: string[];
  recurringSeriesIds: string[];
  programCount: number;
};

export type AgenticInferredWorkTypeSummary = {
  workTypeKey: string;
  label: string;
  serviceCode?: string | null;
  offeringCode?: string | null;
  inferenceSource: 'ticket_subcategory' | 'ticket_title';
  inCatalog: boolean;
  entityKeys: string[];
  evidenceRefs: string[];
};

export type AgenticSubcategoryDecision = {
  decisionKey: string;
  pairedOperationId?: string | null;
  laneKey: string;
  subscriptionId: string;
  serviceCode: string;
  offeringCode: string;
  parentOfferingName?: string | null;
  inferredSubcategoryId: string;
  inferredSubcategoryName: string;
  existingSubcategoryId?: string | null;
  existingSubcategoryName?: string | null;
  status: 'reuse_existing' | 'ready_for_creation' | 'review_required';
  duplicateStatus:
    | 'no_duplicate'
    | 'matched_existing'
    | 'near_duplicate_risk'
    | 'taxonomy_conflict'
    | 'ambiguous_parent';
  reviewRequired: boolean;
  reviewReasonCodes: string[];
  confidence: number;
  rationale: string;
  entityKeys: string[];
  evidenceRefs: string[];
};

export type AgenticKbProcessDecision = {
  decisionKey: string;
  pairedOperationId?: string | null;
  laneKey: string;
  subscriptionId: string;
  serviceCode: string;
  offeringCode: string;
  inferredSubcategoryId: string;
  inferredSubcategoryName: string;
  processTitle: string;
  existingProcessId?: string | null;
  existingProcessTitle?: string | null;
  status: 'reuse_existing' | 'ready_for_creation' | 'review_required' | 'not_needed';
  processStatus:
    | 'sufficient'
    | 'insufficient'
    | 'duplicate_risk'
    | 'blocked_by_taxonomy'
    | 'not_needed';
  reviewRequired: boolean;
  reviewReasonCodes: string[];
  confidence: number;
  rationale: string;
  summary?: string | null;
  requiredInputs: string[];
  orderedSteps: string[];
  qaChecklist: string[];
  hardRules: string[];
  entityKeys: string[];
  evidenceRefs: string[];
};

export type AgenticPlanningSpecialistInvocation = {
  specialistId: string;
  displayName: string;
  stage: AgenticPlanningSpecialistStage;
  influenceStatus: AgenticPlanningSpecialistInfluenceStatus;
  summary?: string | null;
  notes: string[];
};

export type AgenticPlanningSpecialistProvenance = {
  registeredSpecialistIds: string[];
  applicableSpecialistIds: string[];
  invocations: AgenticPlanningSpecialistInvocation[];
  notes: string[];
};

export type AgenticPlanningRunSpecialistSummary = {
  registeredCount: number;
  applicableCount: number;
  appliedCount: number;
  partiallyAppliedCount: number;
  ignoredCount: number;
  specialistIds: string[];
  stagesAffected: AgenticPlanningSpecialistStage[];
  influenceSummaries: string[];
};

export type AgenticBoundaryMetadata = {
  purchasedLanes: AgenticPurchasedLaneSummary[];
  inferredWorkTypes: AgenticInferredWorkTypeSummary[];
  subcategoryDecisions: AgenticSubcategoryDecision[];
  kbProcessDecisions: AgenticKbProcessDecision[];
  executionLanes: AgenticExecutionLaneSummary[];
  immediateScopeKeys: string[];
  backlogScopeKeys: string[];
  specialistProvenance?: AgenticPlanningSpecialistProvenance;
  notes: string[];
};

export type AgenticInputSufficiency = {
  status: 'strong' | 'partial' | 'thin';
  inputDriver: 'strong_input' | 'partial_input' | 'sparse_input';
  planMode: 'full_build' | 'discovery_first' | 'insufficient_input_review';
  discoveryFirstRequired: boolean;
  onboardingProfileFieldCount: number;
  onboardingNoteFieldCount: number;
  recurringNoteCount?: number;
  recurringNoteFieldCount?: number;
  clientProfileFieldCount: number;
  contractLaneCount: number;
  answeredSignals: string[];
  missingSignals: string[];
  requiredSignalsPresent: string[];
  requiredSignalsMissing: string[];
  triggerReasonCodes: string[];
  rationale: string;
};

export type AgenticPlanGraph = {
  graphId: string;
  workstreams: AgenticPlanWorkstream[];
  projects: AgenticPlanProject[];
  milestones: AgenticPlanMilestone[];
  deliverables: AgenticPlanDeliverable[];
  tickets: AgenticPlanTicket[];
  dependencies: Array<{
    fromKey: string;
    toKey: string;
    dependencyType: 'blocks' | 'relates_to';
  }>;
  evidenceRefs: string[];
  assumptions: string[];
  notes: string[];
  packagingMetadata?: AgenticPackagingMetadata;
  boundaryMetadata?: AgenticBoundaryMetadata;
  modeMetadata: {
    mode: AgenticPlanningMode;
    dryRun: true;
    plannerVersion: string;
    promptVersion: string;
    model: string;
    confidence: number;
    inputSufficiency?: AgenticInputSufficiency;
  };
};

export type AgenticValidationResult = {
  ruleName: string;
  status: 'pass' | 'warn' | 'fail';
  severity: 'low' | 'medium' | 'high';
  entityKeys: string[];
  message: string;
  details?: Record<string, unknown>;
  evidenceRefs: string[];
};

export type AgenticWriteOperation = {
  operationId: string;
  action:
    | 'create_project'
    | 'update_project'
    | 'create_milestone'
    | 'create_ticket'
    | 'update_ticket'
    | 'backlog_addition'
    | 'create_subcategory'
    | 'create_kbase_process';
  entityType:
    | 'project'
    | 'milestone'
    | 'ticket'
    | 'service_offering_subcategory'
    | 'kbase_process';
  entityKey: string;
  targetSystem: string;
  payload?: Record<string, unknown>;
  idempotencyKey?: string;
  approvalRequired: true;
  dependsOn: string[];
  pairedOperationId?: string | null;
  existingTargetId?: string | null;
  notes: string[];
};

export type AgenticWritePlan = {
  planId: string;
  dryRun: true;
  targetSystem: string;
  operations: AgenticWriteOperation[];
  approvalGateRequired: true;
  writerMode: 'disabled';
  summary?: {
    projectCreates: number;
    projectUpdates?: number;
    milestoneCreates: number;
    ticketCreates: number;
    ticketUpdates?: number;
    backlogAdditions?: number;
    subcategoryCreates: number;
    kbaseProcessCreates: number;
  };
  warnings: string[];
};

export type AgenticApprovalGate = {
  status: 'review_required' | 'approved' | 'blocked';
  approvalRequired: true;
  reasons: string[];
  featureFlagRequired: string;
  gatePolicyVersion: string;
  approvalRecordId?: string | null;
  reviewNotes: string[];
};

export type AgenticDraftReviewState = {
  reviewStatus: AgenticDraftReviewStatus;
  latestReviewAction?: AgenticDraftReviewAction | null;
  latestReviewReasonCode?: string | null;
  latestReviewNote?: string | null;
  reviewStatusUpdatedAt?: string | null;
  latestReviewedByUserId?: string | null;
  supersededByRunId?: string | null;
};

export type AgenticPlanningRunReviewEvent = {
  reviewEventId: string;
  runId: string;
  orgId: string;
  mode: AgenticPlanningMode;
  action: AgenticDraftReviewAction;
  fromReviewStatus: AgenticDraftReviewStatus;
  toReviewStatus: AgenticDraftReviewStatus;
  note?: string | null;
  reasonCode?: string | null;
  actorType: 'user' | 'system';
  actedByUserId?: string | null;
  actedByDisplayName?: string | null;
  supersededByRunId?: string | null;
  createdAt: string;
};

export type AgenticPlanningApplyOperation = {
  applyOperationId: string;
  applyId: string;
  runId: string;
  orgId: string;
  operationId: string;
  action: AgenticWriteOperation['action'];
  entityType: AgenticWriteOperation['entityType'];
  entityKey: string;
  targetSystem: string;
  idempotencyKey: string;
  status: AgenticApplyOperationStatus;
  attempts: number;
  dependsOn: string[];
  payload: Record<string, unknown>;
  notes: string[];
  pairedOperationId?: string | null;
  reusedFromApplyExecutionId?: string | null;
  reusedFromApplyOperationId?: string | null;
  existingTargetId?: string | null;
  resultTargetId?: string | null;
  resultPayload?: Record<string, unknown> | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgenticPlanningApplyExecution = {
  applyId: string;
  runId: string;
  orgId: string;
  mode: AgenticPlanningMode;
  attemptNumber: number;
  status: AgenticApplyStatus;
  sourceReviewStatus: AgenticDraftReviewStatus;
  requestedByUserId?: string | null;
  totalOperationCount: number;
  succeededOperationCount: number;
  failedOperationCount: number;
  skippedOperationCount: number;
  deferredOperationCount: number;
  errorSummary?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  operations: AgenticPlanningApplyOperation[];
};

export type AgenticPlanningTrigger = {
  triggerId: string;
  orgId: string;
  requestedByUserId?: string | null;
  linkedRunId?: string | null;
  sourceSystem: string;
  sourceKind: string;
  sourceEventId?: string | null;
  sourceNoteId?: string | null;
  sourceMeetingType?: string | null;
  candidateMode?: AgenticPlanningMode | null;
  triggerReason: string;
  dedupeKey: string;
  sourceHash?: string | null;
  sourceUpdatedAt?: string | null;
  status: AgenticTriggerStatus;
  enqueueCount: number;
  dedupeCount: number;
  attemptCount: number;
  latestReasonCode?: string | null;
  latestMessage?: string | null;
  latestError?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type AgenticPlanningTriggerEvent = {
  eventId: string;
  triggerId: string;
  orgId: string;
  linkedRunId?: string | null;
  actedByUserId?: string | null;
  eventType: string;
  status: AgenticTriggerEventStatus;
  reasonCode?: string | null;
  message?: string | null;
  dedupeKey: string;
  createdAt: string;
};

export type AgenticPlanningAttentionItem = {
  attentionItemId: string;
  orgId: string;
  runId?: string | null;
  applyId?: string | null;
  triggerId?: string | null;
  mode?: AgenticPlanningMode | null;
  itemType: AgenticPlanningAttentionItemType;
  status: AgenticPlanningAttentionStatus;
  priority: AgenticPlanningAttentionPriority;
  fingerprintKey: string;
  reasonCode?: string | null;
  reasonSummary: string;
  sourceSummary?: string | null;
  metadata: Record<string, unknown>;
  snoozedUntil?: string | null;
  resolvedAt?: string | null;
  dismissedAt?: string | null;
  obsoleteAt?: string | null;
  createdAt: string;
  updatedAt: string;
  linkedRun?: {
    runId: string;
    mode: AgenticPlanningMode;
    reviewStatus: AgenticDraftReviewStatus;
    status: 'completed' | 'review_required' | 'blocked';
    planClassification: AgenticPlanningClassification;
    summaryText?: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
  linkedApply?: {
    applyId: string;
    status: AgenticApplyStatus;
    attemptNumber: number;
    errorSummary?: string | null;
    createdAt: string;
    updatedAt: string;
    completedAt?: string | null;
  } | null;
  linkedTrigger?: {
    triggerId: string;
    sourceKind: string;
    sourceMeetingType?: string | null;
    candidateMode?: AgenticPlanningMode | null;
    status: AgenticTriggerStatus;
    triggerReason: string;
    latestReasonCode?: string | null;
    latestMessage?: string | null;
    latestError?: string | null;
    sourceUpdatedAt?: string | null;
    createdAt: string;
    updatedAt: string;
  } | null;
};

export type AgenticPlanningAttentionEvent = {
  attentionEventId: string;
  attentionItemId: string;
  orgId: string;
  actedByUserId?: string | null;
  actedByDisplayName?: string | null;
  eventType: 'opened' | 'updated' | 'resolved' | 'dismissed' | 'obsolete';
  fromStatus?: AgenticPlanningAttentionStatus | null;
  toStatus: AgenticPlanningAttentionStatus;
  reasonCode?: string | null;
  note?: string | null;
  createdAt: string;
};

export type AgenticPlanningAttentionCount = {
  itemType: AgenticPlanningAttentionItemType;
  status: AgenticPlanningAttentionStatus;
  count: number;
};

export type AgenticPlanningUsageSummary = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  completionCount: number;
  trackedCompletionCount: number;
  missingUsageCompletionCount: number;
  providers: string[];
  models: string[];
};

export type AgenticPlanningMonthlyUsageModeSummary = {
  mode: AgenticPlanningMode;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  runCount: number;
  trackedRunCount: number;
  completionCount: number;
  trackedCompletionCount: number;
  missingUsageCompletionCount: number;
};

export type AgenticPlanningMonthlyUsageSummary = {
  orgId: string;
  month: string;
  monthLabel: string;
  trackingStatus: 'complete' | 'partial' | 'none';
  estimatedCostStatus?: 'complete' | 'partial' | 'none';
  windowStartAt: string;
  windowEndAt: string;
  measuredThroughAt?: string | null;
  latestRunAt?: string | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  runCount: number;
  trackedRunCount: number;
  untrackedRunCount: number;
  completionCount: number;
  trackedCompletionCount: number;
  missingUsageCompletionCount: number;
  estimatedCostUsd?: number;
  providers: string[];
  models: string[];
  modeBreakdown: AgenticPlanningMonthlyUsageModeSummary[];
};

export type AgenticPlanningRunComparisonCountDelta = {
  left: number;
  right: number;
  delta: number;
};

export type AgenticPlanningRunComparisonEntityChange = {
  identityKey: string;
  entityType:
    | 'project'
    | 'milestone'
    | 'ticket'
    | 'deliverable'
    | 'backlog_item'
    | 'decision'
    | 'validation'
    | 'write_operation'
    | 'taxonomy_kbase_pair'
    | 'ownership';
  label: string;
  matchStrategy:
    | 'existing_id'
    | 'lane_key'
    | 'paired_operation'
    | 'operation_id'
    | 'heuristic'
    | 'planner_key';
  fieldsChanged: string[];
  notes: string[];
};

export type AgenticPlanningRunComparisonOwnershipChange = {
  laneKey: string;
  serviceCode?: string | null;
  leftPrimaryTeamNodeId?: string | null;
  rightPrimaryTeamNodeId?: string | null;
  leftSupportingTeamNodeIds: string[];
  rightSupportingTeamNodeIds: string[];
  notes: string[];
};

export type AgenticPlanningRunComparisonDecisionChange = {
  identityKey: string;
  label: string;
  leftDecisionType?: string | null;
  rightDecisionType?: string | null;
  notes: string[];
};

export type AgenticPlanningRunComparisonValidationChange = {
  identityKey: string;
  ruleName: string;
  leftStatus?: string | null;
  rightStatus?: string | null;
  leftSeverity?: string | null;
  rightSeverity?: string | null;
  leftMessage?: string | null;
  rightMessage?: string | null;
  notes: string[];
};

export type AgenticPlanningRunComparisonWritePlanChange = {
  identityKey: string;
  action: AgenticWriteOperation['action'];
  entityType: AgenticWriteOperation['entityType'];
  label: string;
  matchStrategy:
    | 'existing_id'
    | 'lane_key'
    | 'paired_operation'
    | 'operation_id'
    | 'heuristic'
    | 'planner_key';
  fieldsChanged: string[];
  leftOperationId?: string | null;
  rightOperationId?: string | null;
  notes: string[];
};

export type AgenticPlanningRunComparisonTaxonomyPairChange = {
  pairKey: string;
  label: string;
  matchStrategy:
    | 'existing_id'
    | 'lane_key'
    | 'paired_operation'
    | 'operation_id'
    | 'heuristic'
    | 'planner_key';
  leftSubcategoryId?: string | null;
  rightSubcategoryId?: string | null;
  leftProcessTitle?: string | null;
  rightProcessTitle?: string | null;
  leftActions: AgenticWriteOperation['action'][];
  rightActions: AgenticWriteOperation['action'][];
  fieldsChanged: string[];
  notes: string[];
};

export type AgenticPlanningRunComparison = {
  orgId: string;
  leftRunId: string;
  rightRunId: string;
  comparisonType: AgenticPlanningRunComparisonType;
  modeRelation: AgenticPlanningRunComparisonModeRelation;
  leftMode: AgenticPlanningMode;
  rightMode: AgenticPlanningMode;
  baselineStrategy?: AgenticPlanningRunComparisonBaselineStrategy | null;
  comparability: {
    level: AgenticPlanningRunComparisonComparabilityLevel;
    warnings: string[];
    heuristicMatchingUsed: boolean;
  };
  leftRun: {
    runId: string;
    mode: AgenticPlanningMode;
    status: 'completed' | 'review_required' | 'blocked';
    reviewStatus: AgenticDraftReviewStatus;
    latestReviewAction?: AgenticDraftReviewAction | null;
    planClassification: AgenticPlanningClassification;
    summaryText?: string | null;
    createdAt: string;
    updatedAt: string;
    sourceRefs: string[];
    sourceEventIds: string[];
    linkedTriggers: Array<{
      triggerId: string;
      sourceKind: string;
      sourceMeetingType?: string | null;
      status: AgenticTriggerStatus | string;
      triggerReason: string;
      sourceUpdatedAt?: string | null;
      linkedRunId?: string | null;
    }>;
    latestApply?: {
      applyId: string;
      status: AgenticApplyStatus;
      attemptNumber: number;
      errorSummary?: string | null;
      createdAt: string;
      updatedAt: string;
      completedAt?: string | null;
    } | null;
    applyAttemptCount: number;
    reviewEventCount: number;
    counts: {
      projects: number;
      projectUpdates: number;
      milestones: number;
      tickets: number;
      ticketUpdates: number;
      deliverables: number;
      backlogItems: number;
      immediateProjects: number;
      backlogProjects: number;
      warnings: number;
      failures: number;
      reviewRequiredItems: number;
      fallbackLanes: number;
      unresolvedLanes: number;
      taxonomyPairs: number;
      subcategoryProposals: number;
      kbaseProcessProposals: number;
    };
  };
  rightRun: {
    runId: string;
    mode: AgenticPlanningMode;
    status: 'completed' | 'review_required' | 'blocked';
    reviewStatus: AgenticDraftReviewStatus;
    latestReviewAction?: AgenticDraftReviewAction | null;
    planClassification: AgenticPlanningClassification;
    summaryText?: string | null;
    createdAt: string;
    updatedAt: string;
    sourceRefs: string[];
    sourceEventIds: string[];
    linkedTriggers: Array<{
      triggerId: string;
      sourceKind: string;
      sourceMeetingType?: string | null;
      status: AgenticTriggerStatus | string;
      triggerReason: string;
      sourceUpdatedAt?: string | null;
      linkedRunId?: string | null;
    }>;
    latestApply?: {
      applyId: string;
      status: AgenticApplyStatus;
      attemptNumber: number;
      errorSummary?: string | null;
      createdAt: string;
      updatedAt: string;
      completedAt?: string | null;
    } | null;
    applyAttemptCount: number;
    reviewEventCount: number;
    counts: {
      projects: number;
      projectUpdates: number;
      milestones: number;
      tickets: number;
      ticketUpdates: number;
      deliverables: number;
      backlogItems: number;
      immediateProjects: number;
      backlogProjects: number;
      warnings: number;
      failures: number;
      reviewRequiredItems: number;
      fallbackLanes: number;
      unresolvedLanes: number;
      taxonomyPairs: number;
      subcategoryProposals: number;
      kbaseProcessProposals: number;
    };
  };
  summaryDelta: {
    classificationChanged: boolean;
    reviewStatusChanged: boolean;
    applyStatusChanged: boolean;
    laneSummaryChanged: boolean;
    sourceSummaryChanged: boolean;
    counts: {
      projects: AgenticPlanningRunComparisonCountDelta;
      projectUpdates: AgenticPlanningRunComparisonCountDelta;
      milestones: AgenticPlanningRunComparisonCountDelta;
      tickets: AgenticPlanningRunComparisonCountDelta;
      ticketUpdates: AgenticPlanningRunComparisonCountDelta;
      deliverables: AgenticPlanningRunComparisonCountDelta;
      backlogItems: AgenticPlanningRunComparisonCountDelta;
      warnings: AgenticPlanningRunComparisonCountDelta;
      failures: AgenticPlanningRunComparisonCountDelta;
      reviewRequiredItems: AgenticPlanningRunComparisonCountDelta;
      taxonomyPairs: AgenticPlanningRunComparisonCountDelta;
      subcategoryProposals: AgenticPlanningRunComparisonCountDelta;
      kbaseProcessProposals: AgenticPlanningRunComparisonCountDelta;
    };
    provenance: {
      metadataDrivenLanes: AgenticPlanningRunComparisonCountDelta;
      fallbackLanes: AgenticPlanningRunComparisonCountDelta;
      unresolvedLanes: AgenticPlanningRunComparisonCountDelta;
    };
  };
  changes: {
    addedProjects: AgenticPlanningRunComparisonEntityChange[];
    removedProjects: AgenticPlanningRunComparisonEntityChange[];
    changedProjects: AgenticPlanningRunComparisonEntityChange[];
    addedMilestones: AgenticPlanningRunComparisonEntityChange[];
    removedMilestones: AgenticPlanningRunComparisonEntityChange[];
    changedMilestones: AgenticPlanningRunComparisonEntityChange[];
    addedTickets: AgenticPlanningRunComparisonEntityChange[];
    removedTickets: AgenticPlanningRunComparisonEntityChange[];
    changedTickets: AgenticPlanningRunComparisonEntityChange[];
    addedDeliverables: AgenticPlanningRunComparisonEntityChange[];
    removedDeliverables: AgenticPlanningRunComparisonEntityChange[];
    changedDeliverables: AgenticPlanningRunComparisonEntityChange[];
    backlogChanges: AgenticPlanningRunComparisonEntityChange[];
    ownershipChanges: AgenticPlanningRunComparisonOwnershipChange[];
    decisionChanges: AgenticPlanningRunComparisonDecisionChange[];
    validationChanges: AgenticPlanningRunComparisonValidationChange[];
    writePlanChanges: AgenticPlanningRunComparisonWritePlanChange[];
    taxonomyKbaseChanges: AgenticPlanningRunComparisonTaxonomyPairChange[];
  };
  humanSummary: {
    headline: string;
    bullets: string[];
  };
  generatedAt: string;
};

export type AgenticPlanningRunComparisonSummary = {
  runId: string;
  baselineRunId?: string | null;
  baselineStrategy: AgenticPlanningRunComparisonBaselineStrategy;
  baselineAvailable: boolean;
  message: string;
  comparison?: AgenticPlanningRunComparison | null;
};

export type AgenticTeamAssignmentResult = {
  entityType: 'project' | 'milestone' | 'ticket';
  entityKey: string;
  resolutionStatus: 'assigned' | 'ambiguous' | 'unresolved';
  sourceSignals: Array<{
    source: string;
    teamNodeId?: string | null;
    teamRole?: 'primary' | 'supporting' | null;
    designationType?: string | null;
    owner?: string | null;
    sourceLevel?: 'tier' | 'service' | null;
    sourceRef?: string | null;
  }>;
  requiresManualApproval: boolean;
  ownershipSource?: string | null;
  primaryTeamNodeId?: string | null;
  supportingTeamNodeIds: string[];
  confidence?: number | null;
  reviewReasonCodes: string[];
  notes: string[];
};

export type AgenticFactSet = {
  facts: Array<{
    factId: string;
    category: string;
    statement: string;
    classification: 'explicit' | 'inferred' | 'assumed';
    confidence: number;
    evidenceRefs: string[];
    relatedServiceCodes?: string[];
    scopeRelevance?: 'high' | 'medium' | 'low';
    structuredData?: Record<string, unknown>;
  }>;
  unknowns: Array<{
    id: string;
    question: string;
    evidenceRefs: string[];
    blocking: boolean;
  }>;
  contradictions: Array<{
    id: string;
    description: string;
    severity: 'warn' | 'fail';
    evidenceRefs: string[];
  }>;
  assumptions: Array<{
    id: string;
    statement: string;
    basis: string;
    evidenceRefs: string[];
    confidence: number;
  }>;
  confidenceSummary: {
    overall: number;
    needsHumanReview: boolean;
    rationale: string;
  };
  coverageNotes: string[];
};

export type AgenticContextPack = {
  orgId: string;
  clientId: string;
  routingSignals: {
    nodes: Array<{
      nodeId: string;
      title: string;
      teamLabel?: string | null;
      designationLabel?: string | null;
      meta?: Record<string, unknown>;
      rules?: Record<string, unknown>;
    }>;
  };
  accountMetadata?: {
    orgName: string;
    businessName?: string | null;
    website?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    ownerUserId?: string | null;
    ownerDisplayName?: string | null;
  };
};

export type AgenticRunState = {
  runId: string;
  mode: AgenticPlanningMode;
  status: 'completed' | 'review_required' | 'blocked';
  draftReview: AgenticDraftReviewState;
  createdAt: string;
  updatedAt: string;
  traceRefs: string[];
  specialistSummary?: AgenticPlanningRunSpecialistSummary;
  usageSummary?: AgenticPlanningUsageSummary | null;
};

export type AgenticPlanningRunSummary = {
  runId: string;
  orgId: string;
  mode: AgenticPlanningMode;
  status: 'completed' | 'review_required' | 'blocked';
  executionStatus: 'draft_preview';
  draftReview: AgenticDraftReviewState;
  planClassification: AgenticPlanningClassification;
  createdAt: string;
  updatedAt: string;
  sourceBundleId: string;
  sourceArtifactCount: number;
  sourceRefs: string[];
  sourceEventIds: string[];
  purchasedLaneSummary: AgenticPurchasedLaneSummary[];
  programSummary?: AgenticPlanningRunProgramSummary;
  initiativeSummary?: AgenticPlanningRunInitiativeSummary;
  teamNodeLabels?: Record<string, string>;
  primaryTeamNodeIds: string[];
  supportingTeamNodeIds: string[];
  decisionTypes?: string[];
  counts: {
    projects: number;
    projectUpdates?: number;
    milestones: number;
    tickets: number;
    ticketUpdates?: number;
    backlogItems?: number;
    immediateProjects: number;
    backlogProjects: number;
    warnings: number;
    failures: number;
    reviewRequiredItems: number;
    fallbackLanes: number;
  };
  provenanceSummary: {
    metadataDrivenLanes: number;
    fallbackLanes: number;
    unresolvedLanes: number;
  };
  currentStateOverlapSummary?: {
    matchedProjects: number;
    matchedTickets: number;
    priorPlannerRuns: number;
  };
  specialistSummary?: AgenticPlanningRunSpecialistSummary;
  usageSummary?: AgenticPlanningUsageSummary | null;
  summaryText?: string | null;
};

export type AgenticPlanningProgramSelections = {
  program: AgenticPlanningProgramSummary;
  latestRun: AgenticPlanningRunSummary | null;
  latestApprovedRun: AgenticPlanningRunSummary | null;
  currentDraftRun: AgenticPlanningRunSummary | null;
  latestAppliedRun: AgenticPlanningRunSummary | null;
};

// Legacy initiative contracts remain only for compatibility with older payloads and routes.
export type AgenticPlanningInitiativeSelections = {
  initiative: AgenticPlanningInitiativeSummary;
  latestRun: AgenticPlanningRunSummary | null;
  latestApprovedRun: AgenticPlanningRunSummary | null;
  currentDraftRun: AgenticPlanningRunSummary | null;
  latestAppliedRun: AgenticPlanningRunSummary | null;
};

export type AgenticPlanningDryRunResult = {
  mode: AgenticPlanningMode;
  dryRun: true;
  applied: false;
  runState: AgenticRunState;
  sourceArtifactBundle: AgenticSourceArtifactBundle;
  contextPack: AgenticContextPack;
  factSet: AgenticFactSet;
  planningDecisions: AgenticPlanningDecision[];
  planGraph: AgenticPlanGraph;
  teamAssignmentResults: AgenticTeamAssignmentResult[];
  validationResults: AgenticValidationResult[];
  writePlan: AgenticWritePlan;
  approvalGate: AgenticApprovalGate;
  teamNodeLabels?: Record<string, string>;
  runSummary: AgenticPlanningRunSummary;
};

export type AgenticOnboardingDryRunResult = AgenticPlanningDryRunResult;

export type AgenticOfferingPlaybook = {
  id: string;
  serviceCode: string | null;
  serviceLabel: string | null;
  offeringCode: string;
  title: string;
  summary: string | null;
  objective: string | null;
  scope: string | null;
  requiredInputs: string[];
  expectedOutputs: string[];
  orderedSteps: string[];
  hardRules: string[];
  exampleProjects: string[];
  industryStandards: string[];
  collaborators: Array<{
    serviceCode: string | null;
    teamLabel: string | null;
    rationale: string | null;
    exampleWork: string[];
  }>;
  collaboratorTeamLabels: string[];
  collaboratorServiceCodes: string[];
  recommendedSpecialistIds: string[];
  recommendedSpecialistLabels: string[];
  updatedAt: string;
};

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
const asStringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? Array.from(new Set(value.map((entry) => asText(entry)).filter(Boolean)))
    : [];

function normalizeOfferingPlaybook(raw: unknown): AgenticOfferingPlaybook | null {
  const record = asRecord(raw);
  if (!record) return null;
  const id = asText(record.id);
  const offeringCode = asText(record.offeringCode);
  const title = asText(record.title);
  if (!id || !offeringCode || !title || asText(record.subcategoryId)) {
    return null;
  }
  const plannerContext = asRecord(record.plannerContext);
  const collaboratorEntries = Array.isArray(plannerContext?.collaboratorTeams)
    ? plannerContext?.collaboratorTeams.map((entry) => asRecord(entry)).filter((entry): entry is Record<string, unknown> => Boolean(entry))
    : [];
  const specialistEntries = Array.isArray(plannerContext?.recommendedSpecialists)
    ? plannerContext?.recommendedSpecialists.map((entry) => asRecord(entry)).filter((entry): entry is Record<string, unknown> => Boolean(entry))
    : [];

  return {
    id,
    serviceCode: asText(record.serviceCode) || null,
    serviceLabel: asText(record.serviceLabel) || null,
    offeringCode,
    title,
    summary: asText(record.summary) || null,
    objective: asText(record.objective) || null,
    scope: asText(record.scope) || null,
    requiredInputs: asStringList(record.requiredInputs),
    expectedOutputs: asStringList(record.expectedOutputs),
    orderedSteps: asStringList(record.orderedSteps),
    hardRules: asStringList(record.hardRules),
    exampleProjects: asStringList(plannerContext?.exampleProjects),
    industryStandards: asStringList(plannerContext?.industryStandards),
    collaborators: collaboratorEntries.map((entry) => ({
      serviceCode: asText(entry.serviceCode) || null,
      teamLabel: asText(entry.teamLabel) || null,
      rationale: asText(entry.rationale) || null,
      exampleWork: asStringList(entry.exampleWork),
    })),
    collaboratorTeamLabels: Array.from(
      new Set(
        collaboratorEntries
          .map((entry) => asText(entry.teamLabel))
          .filter(Boolean),
      ),
    ),
    collaboratorServiceCodes: Array.from(
      new Set(
        collaboratorEntries
          .map((entry) => asText(entry.serviceCode))
          .filter(Boolean),
      ),
    ),
    recommendedSpecialistIds: Array.from(
      new Set(
        specialistEntries
          .map((entry) => asText(entry.specialistId))
          .filter(Boolean),
      ),
    ),
    recommendedSpecialistLabels: Array.from(
      new Set(
        specialistEntries
          .map((entry) => asText(entry.displayName) || asText(entry.label))
          .filter(Boolean),
      ),
    ),
    updatedAt: asText(record.updatedAt),
  };
}

function normalizeOnboardingProfile(value: unknown): OrganizationAIAgentOnboardingProfile | null {
  const record = asRecord(value);
  if (!record) return null;
  const normalized = Object.fromEntries(
    Object.entries(record).map(([key, entry]) => [key, asText(entry) || null]),
  ) as OrganizationAIAgentOnboardingProfile;
  return Object.values(normalized).some(Boolean) ? normalized : null;
}

function normalizeScheduleEvent(raw: unknown): OrganizationAIAgentScheduleEvent | null {
  const record = asRecord(raw);
  const id = asText(record?.id);
  if (!id) return null;
  return {
    id,
    orgId: asText(record?.orgId) || null,
    meetingType: asText(record?.meetingType),
    title: asText(record?.title) || 'Untitled meeting',
    startsAt: asText(record?.startsAt),
    endsAt: asText(record?.endsAt),
    timezone: asText(record?.timezone) || 'UTC',
    status: (asText(record?.status) as OrganizationAIAgentScheduleEvent['status']) || 'scheduled',
  };
}

function normalizeMeetingNote(raw: unknown): OrganizationAIAgentMeetingNote | null {
  const record = asRecord(raw);
  const id = asText(record?.id);
  const eventId = asText(record?.eventId);
  if (!id || !eventId) return null;
  return {
    id,
    eventId,
    orgId: asText(record?.orgId) || null,
    summary: asText(record?.summary) || null,
    clientUpdates: asText(record?.clientUpdates) || null,
    decisions: asText(record?.decisions) || null,
    nextSteps: asText(record?.nextSteps) || null,
    internalContext: asText(record?.internalContext) || null,
    onboardingProfile: normalizeOnboardingProfile(record?.onboardingProfile),
    createdAt: asText(record?.createdAt),
    updatedAt: asText(record?.updatedAt),
  };
}

function noteHasOnboardingSource(note: OrganizationAIAgentMeetingNote | null): boolean {
  if (!note) return false;
  if ([note.summary, note.clientUpdates, note.decisions, note.nextSteps, note.internalContext].some((value) => asText(value).length > 0)) {
    return true;
  }
  return Boolean(note.onboardingProfile && Object.values(note.onboardingProfile).some((value) => asText(value).length > 0));
}

function noteHasRecurringSource(note: OrganizationAIAgentMeetingNote | null): note is OrganizationAIAgentMeetingNote {
  if (!note) return false;
  return [note.summary, note.clientUpdates, note.decisions, note.nextSteps, note.internalContext]
    .some((value) => asText(value).length > 0);
}

const RECURRING_PLANNING_MEETING_TYPES = new Set([
  'general_meeting',
  'weekly_sync',
  'monthly_sync',
  'toe_touch',
]);

async function fetchMeetingNote(eventId: string) {
  try {
    const notePayload = await apiFetch<any>(`/api/schedule/events/${encodeURIComponent(eventId)}/note`);
    return notePayload ? normalizeMeetingNote(notePayload) : null;
  } catch {
    return null;
  }
}

export async function fetchLatestOnboardingKickoffSource(
  orgId: string,
): Promise<OrganizationAIAgentKickoffSource | null> {
  const sources = await fetchPlanningMeetingSources(orgId);
  return sources.kickoffSource;
}

export async function fetchOfferingPlaybooks(params?: {
  serviceCode?: string | null;
}): Promise<AgenticOfferingPlaybook[]> {
  const payload = await apiFetch<any>('/api/admin/kbase/processes');
  const processes = Array.isArray(payload?.processes) ? payload.processes : [];
  const filtered = processes
    .map((entry: unknown) => normalizeOfferingPlaybook(entry))
    .filter((entry: AgenticOfferingPlaybook | null): entry is AgenticOfferingPlaybook => Boolean(entry));
  const targetServiceCode = asText(params?.serviceCode).toLowerCase();
  return filtered
    .filter((entry: AgenticOfferingPlaybook) => (
      !targetServiceCode || asText(entry.serviceCode).toLowerCase() === targetServiceCode
    ))
    .sort(
      (left: AgenticOfferingPlaybook, right: AgenticOfferingPlaybook) =>
        new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
    );
}

export async function fetchPlanningMeetingSources(
  orgId: string,
): Promise<OrganizationAIAgentPlanningSources> {
  const payload = await apiFetch<any>(`/api/schedule/events?orgId=${encodeURIComponent(orgId)}`);
  const events = (Array.isArray(payload) ? payload : Array.isArray(payload?.events) ? payload.events : [])
    .map((entry: unknown) => normalizeScheduleEvent(entry))
    .filter((entry: OrganizationAIAgentScheduleEvent | null): entry is OrganizationAIAgentScheduleEvent => Boolean(entry))
    .sort(
      (left: OrganizationAIAgentScheduleEvent, right: OrganizationAIAgentScheduleEvent) =>
        new Date(right.startsAt).getTime() - new Date(left.startsAt).getTime(),
    );

  const kickoffEvents = events.filter(
    (entry: OrganizationAIAgentScheduleEvent) => entry.meetingType === 'intro_call',
  );
  let kickoffSource: OrganizationAIAgentKickoffSource | null = null;
  for (const event of kickoffEvents.slice(0, 5)) {
    const note = await fetchMeetingNote(event.id);
    if (noteHasOnboardingSource(note)) {
      kickoffSource = { event, note };
      break;
    }
  }
  if (!kickoffSource && kickoffEvents[0]) {
    kickoffSource = {
      event: kickoffEvents[0],
      note: await fetchMeetingNote(kickoffEvents[0].id),
    };
  }

  const recurringNoteSources: OrganizationAIAgentRecurringNoteSource[] = [];
  const recurringEvents = events.filter(
    (entry: OrganizationAIAgentScheduleEvent) =>
      RECURRING_PLANNING_MEETING_TYPES.has(entry.meetingType),
  );
  for (const event of recurringEvents) {
    const note = await fetchMeetingNote(event.id);
    if (!noteHasRecurringSource(note)) continue;
    recurringNoteSources.push({ event, note });
    if (recurringNoteSources.length >= 6) break;
  }

  return {
    kickoffSource,
    recurringNoteSources,
  };
}

export async function runOnboardingPlannerDryRun(params: {
  orgId: string;
  scheduleEventId: string;
  serviceContractId?: string;
  offeringCode?: string;
}): Promise<AgenticPlanningDryRunResult> {
  return apiFetch<AgenticPlanningDryRunResult>('/api/agentic/onboarding/runs/dry-run', {
    method: 'POST',
    body: JSON.stringify({
      orgId: params.orgId,
      scheduleEventId: params.scheduleEventId,
      serviceContractId: params.serviceContractId,
      offeringCode: params.offeringCode,
    }),
  });
}

export async function runExistingClientPlannerDryRun(params: {
  orgId: string;
  scheduleEventIds: string[];
  serviceContractId?: string;
  offeringCode?: string;
}): Promise<AgenticPlanningDryRunResult> {
  return apiFetch<AgenticPlanningDryRunResult>('/api/agentic/existing-client/runs/dry-run', {
    method: 'POST',
    body: JSON.stringify({
      orgId: params.orgId,
      scheduleEventIds: params.scheduleEventIds,
      serviceContractId: params.serviceContractId,
      offeringCode: params.offeringCode,
    }),
  });
}

export async function fetchOnboardingPlannerRunHistory(params: {
  orgId: string;
  limit?: number;
}): Promise<AgenticPlanningRunSummary[]> {
  const query = new URLSearchParams({
    orgId: params.orgId,
  });
  if (typeof params.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  return apiFetch<AgenticPlanningRunSummary[]>(`/api/agentic/onboarding/runs?${query.toString()}`);
}

export async function fetchExistingClientPlannerRunHistory(params: {
  orgId: string;
  limit?: number;
}): Promise<AgenticPlanningRunSummary[]> {
  const query = new URLSearchParams({
    orgId: params.orgId,
  });
  if (typeof params.limit === 'number') {
    query.set('limit', String(params.limit));
  }
  return apiFetch<AgenticPlanningRunSummary[]>(`/api/agentic/existing-client/runs?${query.toString()}`);
}

export async function fetchOnboardingPlannerRun(runId: string): Promise<AgenticPlanningDryRunResult> {
  return apiFetch<AgenticPlanningDryRunResult>(`/api/agentic/onboarding/runs/${encodeURIComponent(runId)}`);
}

export async function fetchExistingClientPlannerRun(runId: string): Promise<AgenticPlanningDryRunResult> {
  return apiFetch<AgenticPlanningDryRunResult>(`/api/agentic/existing-client/runs/${encodeURIComponent(runId)}`);
}

function normalizeNullableEntity<T extends Record<string, unknown>>(
  payload: unknown,
  requiredKey: keyof T,
): T | null {
  const record = asRecord(payload);
  if (!record) return null;
  return asText(record[String(requiredKey)]) ? (record as T) : null;
}

function normalizeEntityArray<T>(payload: unknown): T[] {
  return Array.isArray(payload) ? (payload as T[]) : [];
}

export async function fetchPlannerRun(runId: string): Promise<AgenticPlanningDryRunResult> {
  return apiFetch<AgenticPlanningDryRunResult>(`/api/agentic/runs/${encodeURIComponent(runId)}`);
}

export async function fetchPlannerRunSelections(orgId: string): Promise<{
  latestRun: AgenticPlanningRunSummary | null;
  currentRun: AgenticPlanningRunSummary | null;
  latestApprovedRun: AgenticPlanningRunSummary | null;
}> {
  const query = new URLSearchParams({ orgId });
  const [latestRun, currentRun, latestApprovedRun] = await Promise.all([
    apiFetch<AgenticPlanningRunSummary | null>(`/api/agentic/runs/latest?${query.toString()}`),
    apiFetch<AgenticPlanningRunSummary | null>(`/api/agentic/runs/current?${query.toString()}`),
    apiFetch<AgenticPlanningRunSummary | null>(`/api/agentic/runs/latest-approved?${query.toString()}`),
  ]);
  return {
    latestRun: normalizeNullableEntity<AgenticPlanningRunSummary>(latestRun, 'runId'),
    currentRun: normalizeNullableEntity<AgenticPlanningRunSummary>(currentRun, 'runId'),
    latestApprovedRun: normalizeNullableEntity<AgenticPlanningRunSummary>(latestApprovedRun, 'runId'),
  };
}

export async function fetchPlannerMonthlyUsage(params: {
  orgId: string;
  month?: string;
}): Promise<AgenticPlanningMonthlyUsageSummary> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (params.month) query.set('month', params.month);
  return apiFetch<AgenticPlanningMonthlyUsageSummary>(
    `/api/agentic/runs/usage/monthly?${query.toString()}`,
  );
}

export async function fetchPlannerPrograms(params: {
  orgId: string;
  limit?: number;
}): Promise<AgenticPlanningProgramSummary[]> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (typeof params.limit === 'number') query.set('limit', String(params.limit));
  const payload = await apiFetch<AgenticPlanningProgramSummary[] | unknown>(
    `/api/agentic/programs?${query.toString()}`,
  );
  return normalizeEntityArray<AgenticPlanningProgramSummary>(payload);
}

export async function fetchPlannerProgram(programId: string): Promise<AgenticPlanningProgramSummary> {
  return apiFetch<AgenticPlanningProgramSummary>(
    `/api/agentic/programs/${encodeURIComponent(programId)}`,
  );
}

export async function fetchPlannerProgramRuns(params: {
  programId: string;
  limit?: number;
}): Promise<AgenticPlanningRunSummary[]> {
  const query = new URLSearchParams();
  if (typeof params.limit === 'number') query.set('limit', String(params.limit));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  const payload = await apiFetch<AgenticPlanningRunSummary[] | unknown>(
    `/api/agentic/programs/${encodeURIComponent(params.programId)}/runs${suffix}`,
  );
  return normalizeEntityArray<AgenticPlanningRunSummary>(payload);
}

export async function fetchPlannerProgramSelections(
  programId: string,
): Promise<AgenticPlanningProgramSelections> {
  return apiFetch<AgenticPlanningProgramSelections>(
    `/api/agentic/programs/${encodeURIComponent(programId)}/selections`,
  );
}

export async function fetchPlannerProgramRecurringSeries(
  programId: string,
): Promise<AgenticPlanningRecurringSeriesSummary[]> {
  const payload = await apiFetch<AgenticPlanningRecurringSeriesSummary[] | unknown>(
    `/api/agentic/programs/${encodeURIComponent(programId)}/recurring-series`,
  );
  return normalizeEntityArray<AgenticPlanningRecurringSeriesSummary>(payload);
}

// Legacy initiative-named fetchers remain only as compatibility aliases to canonical Program routes.
export async function fetchPlannerInitiatives(params: {
  orgId: string;
  limit?: number;
}): Promise<AgenticPlanningProgramSummary[]> {
  return fetchPlannerPrograms(params);
}

export async function fetchPlannerInitiative(initiativeId: string): Promise<AgenticPlanningProgramSummary> {
  return fetchPlannerProgram(initiativeId);
}

export async function fetchPlannerInitiativeRuns(params: {
  initiativeId: string;
  limit?: number;
}): Promise<AgenticPlanningRunSummary[]> {
  return fetchPlannerProgramRuns({
    programId: params.initiativeId,
    limit: params.limit,
  });
}

export async function fetchPlannerInitiativeSelections(
  initiativeId: string,
): Promise<AgenticPlanningProgramSelections> {
  return fetchPlannerProgramSelections(initiativeId);
}

export async function fetchPlannerInitiativeRecurringSeries(
  initiativeId: string,
): Promise<AgenticPlanningRecurringSeriesSummary[]> {
  return fetchPlannerProgramRecurringSeries(initiativeId);
}

export async function fetchPlannerRunReviewHistory(runId: string): Promise<AgenticPlanningRunReviewEvent[]> {
  const payload = await apiFetch<AgenticPlanningRunReviewEvent[] | unknown>(
    `/api/agentic/runs/${encodeURIComponent(runId)}/reviews`,
  );
  return normalizeEntityArray<AgenticPlanningRunReviewEvent>(payload);
}

export async function reviewPlannerRun(params: {
  runId: string;
  action: AgenticDraftReviewAction;
  note?: string;
  reasonCode?: string;
}): Promise<AgenticPlanningDryRunResult> {
  return apiFetch<AgenticPlanningDryRunResult>(`/api/agentic/runs/${encodeURIComponent(params.runId)}/reviews`, {
    method: 'POST',
    body: JSON.stringify({
      action: params.action,
      note: asText(params.note) || undefined,
      reasonCode: asText(params.reasonCode) || undefined,
    }),
  });
}

export async function fetchPlannerRunLatestApply(runId: string): Promise<AgenticPlanningApplyExecution | null> {
  const payload = await apiFetch<AgenticPlanningApplyExecution | null>(
    `/api/agentic/runs/${encodeURIComponent(runId)}/apply`,
  );
  return normalizeNullableEntity<AgenticPlanningApplyExecution>(payload, 'applyId');
}

export async function fetchPlannerRunApplyHistory(runId: string): Promise<AgenticPlanningApplyExecution[]> {
  const payload = await apiFetch<AgenticPlanningApplyExecution[] | unknown>(
    `/api/agentic/runs/${encodeURIComponent(runId)}/applies`,
  );
  return normalizeEntityArray<AgenticPlanningApplyExecution>(payload);
}

export async function applyPlannerRun(runId: string): Promise<AgenticPlanningApplyExecution> {
  return apiFetch<AgenticPlanningApplyExecution>(`/api/agentic/runs/${encodeURIComponent(runId)}/apply`, {
    method: 'POST',
  });
}

export async function fetchPlannerTriggers(params: {
  orgId: string;
  limit?: number;
  mode?: AgenticPlanningMode;
  status?: AgenticTriggerStatus;
}): Promise<AgenticPlanningTrigger[]> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (typeof params.limit === 'number') query.set('limit', String(params.limit));
  if (params.mode) query.set('mode', params.mode);
  if (params.status) query.set('status', params.status);
  const payload = await apiFetch<AgenticPlanningTrigger[] | unknown>(`/api/agentic/triggers?${query.toString()}`);
  return normalizeEntityArray<AgenticPlanningTrigger>(payload);
}

export async function fetchPlannerAttentionItems(params: {
  orgId: string;
  limit?: number;
  itemType?: AgenticPlanningAttentionItemType;
  status?: AgenticPlanningAttentionStatus;
  actionableOnly?: boolean;
}): Promise<AgenticPlanningAttentionItem[]> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (typeof params.limit === 'number') query.set('limit', String(params.limit));
  if (params.itemType) query.set('itemType', params.itemType);
  if (params.status) query.set('status', params.status);
  if (typeof params.actionableOnly === 'boolean') query.set('actionableOnly', String(params.actionableOnly));
  const payload = await apiFetch<AgenticPlanningAttentionItem[] | unknown>(
    `/api/agentic/attention?${query.toString()}`,
  );
  return normalizeEntityArray<AgenticPlanningAttentionItem>(payload);
}

export async function fetchPlannerAttentionCounts(params: {
  orgId: string;
  actionableOnly?: boolean;
}): Promise<AgenticPlanningAttentionCount[]> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (typeof params.actionableOnly === 'boolean') query.set('actionableOnly', String(params.actionableOnly));
  const payload = await apiFetch<AgenticPlanningAttentionCount[] | unknown>(
    `/api/agentic/attention/counts?${query.toString()}`,
  );
  return normalizeEntityArray<AgenticPlanningAttentionCount>(payload);
}

export async function fetchPlannerAttentionEvents(
  attentionItemId: string,
): Promise<AgenticPlanningAttentionEvent[]> {
  const payload = await apiFetch<AgenticPlanningAttentionEvent[] | unknown>(
    `/api/agentic/attention/${encodeURIComponent(attentionItemId)}/events`,
  );
  return normalizeEntityArray<AgenticPlanningAttentionEvent>(payload);
}

export async function updatePlannerAttentionItem(params: {
  attentionItemId: string;
  action: AgenticPlanningAttentionAction;
  note?: string;
}): Promise<AgenticPlanningAttentionItem> {
  return apiFetch<AgenticPlanningAttentionItem>(
    `/api/agentic/attention/${encodeURIComponent(params.attentionItemId)}/actions`,
    {
      method: 'POST',
      body: JSON.stringify({
        action: params.action,
        note: asText(params.note) || undefined,
      }),
    },
  );
}

export async function fetchPlannerTriggerEvents(
  triggerId: string,
  limit = 20,
): Promise<AgenticPlanningTriggerEvent[]> {
  const query = new URLSearchParams({ limit: String(limit) });
  const payload = await apiFetch<AgenticPlanningTriggerEvent[] | unknown>(
    `/api/agentic/triggers/${encodeURIComponent(triggerId)}/events?${query.toString()}`,
  );
  return normalizeEntityArray<AgenticPlanningTriggerEvent>(payload);
}

export async function fetchPlannerRunComparisonSummary(
  runId: string,
  baseline?: AgenticPlanningRunComparisonBaseline,
): Promise<AgenticPlanningRunComparisonSummary> {
  const query = new URLSearchParams();
  if (baseline) query.set('baseline', baseline);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return apiFetch<AgenticPlanningRunComparisonSummary>(
    `/api/agentic/runs/${encodeURIComponent(runId)}/comparison-summary${suffix}`,
  );
}

export async function fetchPlannerLatestVsPreviousComparison(params: {
  orgId: string;
  mode?: AgenticPlanningMode;
}): Promise<AgenticPlanningRunComparisonSummary> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (params.mode) query.set('mode', params.mode);
  return apiFetch<AgenticPlanningRunComparisonSummary>(
    `/api/agentic/runs/compare/latest-vs-previous?${query.toString()}`,
  );
}

export async function fetchPlannerLatestVsApprovedComparison(params: {
  orgId: string;
  mode?: AgenticPlanningMode;
}): Promise<AgenticPlanningRunComparisonSummary> {
  const query = new URLSearchParams({ orgId: params.orgId });
  if (params.mode) query.set('mode', params.mode);
  return apiFetch<AgenticPlanningRunComparisonSummary>(
    `/api/agentic/runs/compare/latest-vs-approved?${query.toString()}`,
  );
}
